/**
 * Admin — Polyploid module waitlist.
 * Lists express-interest signups captured on the Molecular Genetics
 * polyploid tab. Admin-gated (backend 403 for non-admins). CSV export.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Download, RefreshCw, ArrowLeft, Users } from 'lucide-react';

export default function AdminWaitlist() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/waitlist`, { withCredentials: true });
      setRows(resp.data.rows || []);
    } catch (e) {
      const status = e.response?.status;
      setError(status === 403 ? 'Admin access required.' : (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const downloadCsv = async () => {
    try {
      const resp = await axios.get(`${API}/waitlist/export.csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'polyploid_waitlist.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('CSV export failed');
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }} data-testid="admin-waitlist-page">
      <button
        onClick={() => navigate('/admin/failures')}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', marginBottom: 16, fontSize: 13 }}
        data-testid="admin-waitlist-back-btn"
      >
        <ArrowLeft size={14} /> Admin failures
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h1 className="font-display" style={{ fontSize: 26, color: '#1B4F47', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Users size={24} /> Polyploid Module Waitlist
          </h1>
          <p style={{ color: '#6B7280', fontSize: 14, marginTop: 4 }}>
            Breeder express-interest signups for the parked polysomic autopolyploid compute module.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={fetchRows} disabled={loading} style={btnGhost} data-testid="admin-waitlist-refresh-btn">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={downloadCsv} disabled={rows.length === 0} style={btnPrimary} data-testid="admin-waitlist-csv-btn">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #E7E5E0', borderRadius: 10, padding: '14px 20px', marginBottom: 20, display: 'inline-block' }} data-testid="admin-waitlist-count">
        <div style={{ fontSize: 28, fontWeight: 700, color: '#1F6B47' }}>{rows.length}</div>
        <div style={{ fontSize: 12, color: '#6B7280' }}>total signups</div>
      </div>

      {error && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: 14, borderRadius: 8 }} data-testid="admin-waitlist-error">{error}</div>
      )}

      {!error && !loading && rows.length === 0 && (
        <div style={{ background: '#F9FAFB', color: '#6B7280', padding: 32, borderRadius: 8, textAlign: 'center' }} data-testid="admin-waitlist-empty">
          No signups yet.
        </div>
      )}

      {!error && rows.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #E7E5E0', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="admin-waitlist-table">
            <thead>
              <tr style={{ background: '#F1F5F9', textAlign: 'left' }}>
                <th style={th}>Email</th>
                <th style={th}>Name</th>
                <th style={th}>Organisation</th>
                <th style={th}>Crop / ploidy</th>
                <th style={th}>Message</th>
                <th style={th}>×</th>
                <th style={th}>Signed up</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #E7E5E0' }} data-testid={`admin-waitlist-row-${r.email}`}>
                  <td style={td}>{r.email}</td>
                  <td style={td}>{r.name || '—'}</td>
                  <td style={td}>{r.organisation || '—'}</td>
                  <td style={td}>{r.crop_interest || '—'}</td>
                  <td style={{ ...td, maxWidth: 280, whiteSpace: 'normal' }}>{r.message || '—'}</td>
                  <td style={td}>{r.submission_count}</td>
                  <td style={td}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px 14px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' };
const td = { padding: '10px 14px', color: '#4B5563', whiteSpace: 'nowrap' };
const btnGhost = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid #E7E5E0', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13 };
const btnPrimary = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', background: '#1F6B47', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13 };
